import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import ListsManager from '../services/ListsManager';
import { PostDetailView } from './ListDetailPanel';
import {
  FaSearch, FaTimes, FaGlobe, FaTwitter, FaTelegram, FaTiktok,
  FaInstagram, FaNewspaper, FaFacebookF, FaHeart, FaEye,
  FaSortAmountDown, FaSortAmountUp, FaTv,
} from 'react-icons/fa';
import { useOrg } from '../hooks/useOrg';
import { useTheme } from '../theme/ThemeContext';
import { EMOTION_COLORS } from '../lib/utils';
import i18n from '../i18n';

let T = {};
let isDark = false;

const PLATFORM_ICONS = {
  twitter: FaTwitter, telegram: FaTelegram,
  tiktok: FaTiktok, instagram: FaInstagram,
  facebook_group: FaFacebookF,
  rss: FaNewspaper, news: FaNewspaper,
  tv: FaTv,
};
const PLATFORM_COLORS = {
  twitter: '#1da1f2', telegram: '#0088cc',
  tiktok: '#ff0050', instagram: '#e1306c',
  facebook_group: '#1877F2',
  rss: '#f97316', news: '#6366f1',
  tv: '#9f1239',
};
const ALL_PLATFORMS = ['twitter', 'telegram', 'tiktok', 'instagram', 'facebook_group', 'rss', 'news', 'tv'];
const ALL_EMOTIONS = ['joy', 'anger', 'sadness', 'fear', 'surprise', 'disgust', 'neutral'];
const SORT_OPTIONS = ['date', 'popularity', 'sentiment', 'risk'];

function formatStat(n) {
  if (!n && n !== 0) return null;
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n);
}

function buildPostUrl(item) {
  if (item.url) return item.url;
  if (item.source === 'twitter' && item.message_id && item.username)
    return `https://twitter.com/${item.username}/status/${item.message_id}`;
  if (item.source === 'tiktok' && item.username && item.message_id)
    return `https://www.tiktok.com/@${item.username}/video/${item.message_id}`;
  if (item.source === 'instagram' && item.username)
    return `https://www.instagram.com/${item.username}/`;
  if (item.source === 'facebook_group' && item.message_id)
    return `https://www.facebook.com/${item.message_id}`;
  return null;
}

function getPopularityScore(item) {
  return (item.view_count || 0) + (item.like_count || item.likes || 0) * 3
    + (item.comment_count || item.reply_count || 0) * 5
    + (item.retweet_count || item.share_count || 0) * 4;
}

function getBrandRiskScore(item) {
  const sent = item.sentiment_score ?? 0;
  const pop = getPopularityScore(item);
  return sent < 0 ? Math.abs(sent) * (1 + Math.log10(Math.max(pop, 1))) : 0;
}

// ─── Search Result Tile ─────────────────────────────────────────────────────────
function SearchResultTile({ item, isSelected, onSelect, highlightText }) {
  const platform = (item.source || 'twitter').toLowerCase();
  const PlatformIcon = PLATFORM_ICONS[platform] || FaGlobe;
  const platformColor = PLATFORM_COLORS[platform] || '#8e8e93';
  const handle = item.username || '';
  const displayName = item.author_name || handle || 'Unknown';
  const content = item.tweet_content || item.content || item.translated_content || '';
  const avatarUrl = item.author_profile_image_url;
  const initial = (displayName)[0]?.toUpperCase() || '?';
  const images = item.tweet_images || [];
  const views = formatStat(item.view_count);
  const likes = formatStat(item.like_count || item.likes);
  const sentiment = item.sentiment_score;
  const emotion = item.dominant_emotion;

  const relativeTime = item.created_at ? (() => {
    const diff = Date.now() - new Date(item.created_at);
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d`;
    return new Date(item.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' });
  })() : '';

  const sentimentColor = sentiment != null
    ? sentiment > 0.3 ? '#30d158' : sentiment < -0.3 ? '#ff453a' : '#8e8e93'
    : null;

  return (
    <div
      onClick={() => onSelect?.(item)}
      style={{
        padding: '12px 14px',
        borderBottom: `1px solid ${T.border}`,
        cursor: 'pointer',
        background: isSelected ? (isDark ? `${T.primary}12` : '#f0f9ff') : 'transparent',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        transition: 'background 0.12s ease',
      }}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt="" style={{
          width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
        }} onError={(e) => { e.target.style.display = 'none'; }} />
      ) : (
        <div style={{
          width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
          background: `${platformColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: platformColor, fontSize: 13, fontWeight: 700,
        }}>{initial}</div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
          <span style={{ color: T.text, fontSize: 12, fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {displayName}
          </span>
          {handle && handle !== displayName && (
            <span style={{ color: T.textMuted, fontSize: 11, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
              @{handle}
            </span>
          )}
          <PlatformIcon size={10} color={platformColor} style={{ flexShrink: 0 }} />
          <span style={{ marginInlineStart: 'auto', color: T.textMuted, fontSize: 10, flexShrink: 0 }}>{relativeTime}</span>
        </div>

        {content && (
          <div style={{
            color: T.textSec, fontSize: 12, lineHeight: '17px',
            overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
            marginBottom: 6,
          }}>
            {highlightText ? (() => {
              const escaped = highlightText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const parts = content.split(new RegExp(`(${escaped})`, 'gi'));
              return parts.map((part, i) =>
                part.toLowerCase() === highlightText.toLowerCase()
                  ? <mark key={i} style={{ background: 'rgba(250,204,21,0.35)', color: 'inherit', borderRadius: 2, padding: '0 1px' }}>{part}</mark>
                  : part
              );
            })() : content}
          </div>
        )}

        {images.length > 0 && (
          <img src={images[0]} alt="" style={{
            maxHeight: 48, maxWidth: 80, borderRadius: 6, objectFit: 'cover',
            marginBottom: 6, display: 'block',
          }} onError={(e) => { e.target.style.display = 'none'; }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {likes && (
            <span style={{ color: T.textMuted, fontSize: 10, display: 'flex', alignItems: 'center', gap: 2 }}>
              <FaHeart size={8} color="#ff2d55" /> {likes}
            </span>
          )}
          {views && (
            <span style={{ color: T.textMuted, fontSize: 10, display: 'flex', alignItems: 'center', gap: 2 }}>
              <FaEye size={8} color="#8e8e93" /> {views}
            </span>
          )}
          {sentimentColor && (
            <span style={{
              width: 6, height: 6, borderRadius: '50%', background: sentimentColor, flexShrink: 0,
            }} />
          )}
          {emotion && emotion !== 'neutral' && (
            <span style={{
              fontSize: 9, fontWeight: 600, color: EMOTION_COLORS[emotion] || '#8e8e93',
              textTransform: 'capitalize',
            }}>{emotion}</span>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Main Search Screen ─────────────────────────────────────────────────────────
const SearchScreen = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { activeOrg } = useOrg();
  const isRTL = i18n.language === 'he';

  isDark = theme.bg === '#050507' || (theme.bg && theme.bg.startsWith('#0'));
  Object.assign(T, isDark ? {
    bg:       '#0f1117',
    card:     '#1a1d27',
    surface:  '#22252f',
    border:   '#2e3140',
    text:     '#f1f5f9',
    textSec:  '#94a3b8',
    textMuted:'#64748b',
    primary:  '#38bdf8',
    success:  '#4ade80',
    warning:  '#fbbf24',
    error:    '#f87171',
    purple:   '#a78bfa',
  } : {
    bg:       '#ffffff',
    card:     '#ffffff',
    surface:  '#f9fafb',
    border:   '#e5e7eb',
    text:     '#111827',
    textSec:  '#374151',
    textMuted:'#9ca3af',
    primary:  '#0288d1',
    success:  '#16a34a',
    warning:  '#d97706',
    error:    '#dc2626',
    purple:   '#7c3aed',
  });

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [platformTotals, setPlatformTotals] = useState({});

  // Filters state
  const [activePlatforms, setActivePlatforms] = useState([...ALL_PLATFORMS]);
  const [activeEmotions, setActiveEmotions] = useState([]);
  const [sentimentRange, setSentimentRange] = useState([-1, 1]);
  const [authorFilter, setAuthorFilter] = useState('');
  const [minLikes, setMinLikes] = useState('');
  const [minViews, setMinViews] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedList, setSelectedList] = useState('');
  const [selectedListRoles, setSelectedListRoles] = useState([]);
  const [lists, setLists] = useState([]);

  // Pagination
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const PAGE_SIZE = 30;

  // Draggable column widths
  const [filtersW, setFiltersW] = useState(240);
  const [resultsW, setResultsW] = useState(380);
  const dragRef = useRef(null);
  const dragStartRef = useRef(null);

  const startDrag = (col) => (e) => {
    e.preventDefault();
    dragRef.current = col;
    dragStartRef.current = { x: e.clientX, filtersW, resultsW };
    const onMove = (mv) => {
      const rawDx = mv.clientX - dragStartRef.current.x;
      const dx = isRTL ? -rawDx : rawDx;
      if (dragRef.current === 'filters') {
        setFiltersW(Math.max(180, Math.min(360, dragStartRef.current.filtersW + dx)));
      } else {
        setResultsW(Math.max(260, Math.min(560, dragStartRef.current.resultsW + dx)));
      }
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    if (activeOrg?.id) {
      ListsManager.fetchLists(activeOrg.id).then(data => {
        if (Array.isArray(data)) setLists(data);
      }).catch(() => {});
    }
  }, [activeOrg?.id]);

  const executeSearch = useCallback(async (searchQuery, newOffset = 0, append = false) => {

    if (newOffset === 0) {
      setIsLoading(true);
      setHasSearched(true);
    } else {
      setIsLoadingMore(true);
    }

    const since = dateFrom || null;
    const search = searchQuery.trim() || null;
    const limit = PAGE_SIZE;

    // Build server-side filters object
    const filters = {};
    if (activeOrg?.id) filters.org_id = activeOrg.id;
    if (activeEmotions.length > 0) filters.emotion = activeEmotions.join(',');
    if (sentimentRange[0] > -1) filters.sentiment_min = sentimentRange[0];
    if (sentimentRange[1] < 1) filters.sentiment_max = sentimentRange[1];
    if (selectedList) filters.list_name = selectedList;
    if (selectedListRoles.length > 0 && selectedListRoles.length < 3) filters.list_role = selectedListRoles.join(',');
    if (minLikes && Number(minLikes) > 0) filters.min_likes = Number(minLikes);
    if (minViews && Number(minViews) > 0) filters.min_views = Number(minViews);

    try {
      const fetchers = [];
      const platformsToFetch = activePlatforms;

      // Each fetcher resolves to { platform, total, items }. `total` is the
      // server-side count of ALL matching rows (from the X-Total-Count header,
      // stamped on the array as `.totalCount`), captured before `.map` discards it.
      const tag = (platform, promise) => fetchers.push(
        promise.then(arr => ({
          platform,
          total: arr.totalCount,
          items: arr.map(item => ({ ...item, source: item.source || platform })),
        }))
      );

      if (platformsToFetch.includes('twitter')) {
        tag('twitter', ListsManager.fetchAllTweetsForListener(since, limit, newOffset, search, 'created_at', filters));
      }
      if (platformsToFetch.includes('telegram')) {
        tag('telegram', ListsManager.fetchAllTelegramMessagesForListener(since, limit, newOffset, search, 'created_at', filters));
      }
      if (platformsToFetch.includes('tiktok')) {
        tag('tiktok', ListsManager.fetchAllTiktoksForListener(since, limit, newOffset, search, 'created_at', filters));
      }
      if (platformsToFetch.includes('instagram')) {
        tag('instagram', ListsManager.fetchAllInstagramsForListener(since, limit, newOffset, search, 'created_at', filters));
      }
      if (platformsToFetch.includes('facebook_group')) {
        tag('facebook_group', ListsManager.fetchAllFacebookGroupPostsForListener(since, limit, newOffset, search, 'created_at', filters));
      }
      if (platformsToFetch.includes('rss')) {
        tag('rss', ListsManager.fetchAllRssItemsForListener(since, limit, newOffset, search, 'created_at', filters));
      }
      if (platformsToFetch.includes('news')) {
        tag('news', ListsManager.fetchAllWebzArticlesForListener(since, limit, newOffset, search, 'created_at', filters));
      }
      if (platformsToFetch.includes('tv')) {
        tag('tv', ListsManager.fetchAllTvSegmentsForListener(since, limit, newOffset, search, 'created_at', filters));
      }

      const responses = await Promise.all(fetchers);
      let merged = responses.flatMap(r => r.items);

      // Capture server-side per-platform totals on the first page so the counter
      // reflects all matching messages, not just the loaded page.
      if (newOffset === 0) {
        const totals = {};
        responses.forEach(r => {
          if (typeof r.total === 'number') totals[r.platform] = r.total;
        });
        setPlatformTotals(totals);
      }

      // Deduplicate by source + id, merging list info
      const dedupe = (items) => {
        const map = new Map();
        items.forEach(item => {
          const key = `${item.source || 'twitter'}-${item.tweet_id || item.id}`;
          if (map.has(key)) {
            const existing = map.get(key);
            if (item.list_name && !existing._all_lists.some(l => l.name === item.list_name)) {
              existing._all_lists.push({ name: item.list_name, id: item.list_id });
            }
          } else {
            const entry = { ...item, _all_lists: [] };
            if (item.list_name) entry._all_lists.push({ name: item.list_name, id: item.list_id });
            map.set(key, entry);
          }
        });
        return Array.from(map.values());
      };

      if (append) {
        setResults(prev => dedupe([...prev, ...merged]));
      } else {
        setResults(dedupe(merged));
      }
      setHasMore(merged.length >= limit);
      setOffset(newOffset + limit);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [activePlatforms, dateFrom, activeEmotions, sentimentRange, selectedList, selectedListRoles, minLikes, minViews, activeOrg?.id]);

  // Apply client-side filters (only for fields not handled server-side)
  useEffect(() => {
    let filtered = [...results];

    // Platform filter — guarantees no posts from unselected platforms leak through
    filtered = filtered.filter(item => activePlatforms.includes((item.source || 'twitter').toLowerCase()));

    // Author filter
    if (authorFilter.trim()) {
      const lowerAuthor = authorFilter.toLowerCase();
      filtered = filtered.filter(item =>
        (item.username || '').toLowerCase().includes(lowerAuthor) ||
        (item.author_name || '').toLowerCase().includes(lowerAuthor)
      );
    }

    // Engagement filters — applied client-side for consistency across all platforms
    if (minLikes && Number(minLikes) > 0) {
      const min = Number(minLikes);
      filtered = filtered.filter(item => (item.like_count || item.likes || 0) >= min);
    }
    if (minViews && Number(minViews) > 0) {
      const min = Number(minViews);
      filtered = filtered.filter(item => (item.view_count || 0) >= min);
    }

    // Date to (client-side — backend only supports 'since')
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => {
        if (!item.created_at) return true;
        return new Date(item.created_at) <= toDate;
      });
    }

    // Sort (client-side across merged results from multiple platforms)
    filtered.sort((a, b) => {
      let cmp;
      switch (sortBy) {
        case 'popularity': cmp = getPopularityScore(a) - getPopularityScore(b); break;
        case 'sentiment': cmp = (a.sentiment_score || 0) - (b.sentiment_score || 0); break;
        case 'risk': cmp = getBrandRiskScore(a) - getBrandRiskScore(b); break;
        default: cmp = new Date(a.created_at || 0) - new Date(b.created_at || 0); break;
      }
      return sortAsc ? cmp : -cmp;
    });

    setFilteredResults(filtered);
  }, [results, activePlatforms, authorFilter, minLikes, minViews, dateTo, sortBy, sortAsc]);

  // Initial load — fetch latest posts on mount
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (activeOrg?.id && !initialLoadDone.current) {
      initialLoadDone.current = true;
      executeSearch('', 0, false);
    }
  }, [activeOrg?.id]);

  // Debounced search — re-triggers on query or any server-side filter change
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Clear stale results immediately so old posts from wrong platforms don't show
    setResults([]);
    setFilteredResults([]);
    setPlatformTotals({});
    setOffset(0);
    setHasMore(false);
    debounceRef.current = setTimeout(() => {
      executeSearch(query, 0, false);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [query, activePlatforms, dateFrom, activeEmotions, sentimentRange, selectedList, selectedListRoles, minLikes, minViews]);

  const loadMore = () => {
    if (isLoadingMore || !hasMore) return;
    executeSearch(query, offset, true);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMore();
    }
  };

  const togglePlatform = (p) => {
    setActivePlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  const toggleEmotion = (e) => {
    setActiveEmotions(prev =>
      prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]
    );
  };

  const clearFilters = () => {
    setActivePlatforms([...ALL_PLATFORMS]);
    setActiveEmotions([]);
    setSentimentRange([-1, 1]);
    setAuthorFilter('');
    setMinLikes('');
    setMinViews('');
    setDateFrom('');
    setDateTo('');
    setSelectedList('');
    setSelectedListRoles([]);
  };

  const activeFilterCount = [
    activePlatforms.length < ALL_PLATFORMS.length,
    activeEmotions.length > 0,
    sentimentRange[0] > -1 || sentimentRange[1] < 1,
    authorFilter.trim(),
    minLikes,
    minViews,
    dateFrom,
    dateTo,
    selectedList,
    selectedListRoles.length > 0,
  ].filter(Boolean).length;

  // Per-page loaded counts (fallback when a server total isn't available).
  const loadedCounts = {};
  filteredResults.forEach(item => {
    const src = item.source || 'twitter';
    loadedCounts[src] = (loadedCounts[src] || 0) + 1;
  });

  // Server-side totals (all matching rows) take precedence over the loaded page;
  // only show platforms that are currently active.
  const platformCounts = {};
  activePlatforms.forEach(src => {
    const total = platformTotals[src];
    const count = typeof total === 'number' ? total : (loadedCounts[src] || 0);
    if (count > 0) platformCounts[src] = count;
  });

  const totalCount = Object.values(platformCounts).reduce((s, n) => s + n, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: T.bg, direction: isRTL ? 'rtl' : 'ltr' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        .search-input-wrap {
          display: flex; align-items: center; gap: 8;
          background: ${T.card}; border: 1px solid ${T.border};
          border-radius: 8px; padding: 0 14px; height: 44px;
          flex: 1; transition: border-color 0.15s ease;
        }
        .search-input-wrap:focus-within {
          border-color: ${T.primary};
        }
        .search-input-wrap input {
          flex: 1; background: none; border: none; outline: none;
          color: ${T.text}; font-size: 14px; direction: inherit;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .search-input-wrap input::placeholder { color: ${T.textMuted}; }
        .srch-pill {
          padding: 5px 10px; border-radius: 20px; font-size: 10px; font-weight: 600;
          cursor: pointer; transition: border-color 0.12s ease; border: 1px solid ${T.border};
          white-space: nowrap; user-select: none;
        }
        .srch-pill.active { border-color: ${T.primary}; background: ${isDark ? `${T.primary}12` : '#f0f9ff'}; color: ${T.primary}; }
        .srch-pill:not(.active) { background: ${T.surface}; color: ${T.textSec}; }
        .srch-pill:hover { border-color: ${isDark ? '#7dd3fc' : '#7dd3fc'}; }
        .srch-sort-pill {
          padding: 5px 10px; border-radius: 8px; font-size: 11px; font-weight: 600;
          cursor: pointer; transition: border-color 0.12s ease;
          white-space: nowrap; user-select: none;
        }
        .srch-sort-pill.active { background: ${T.primary}; color: #fff; border: 1px solid ${T.primary}; }
        .srch-sort-pill:not(.active) { background: ${T.surface}; color: ${T.textSec}; border: 1px solid ${T.border}; }
        .srch-results-list::-webkit-scrollbar { width: 4px; }
        .srch-results-list::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
        .srch-filter-input {
          background: ${T.surface}; border: 1px solid ${T.border}; border-radius: 8px;
          padding: 6px 10px; font-size: 11px; color: ${T.text}; outline: none;
          width: 100%; box-sizing: border-box; direction: inherit; max-width: 100%;
        }
        .srch-filter-input:focus { border-color: ${T.primary}; }
        .srch-filter-input::placeholder { color: ${T.textMuted}; }
        .srch-filter-select {
          background: ${T.surface}; border: 1px solid ${T.border}; border-radius: 8px;
          padding: 6px 10px; font-size: 11px; color: ${T.text}; outline: none;
          width: 100%; box-sizing: border-box; direction: inherit; appearance: none; max-width: 100%;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: ${isRTL ? 'left 10px center' : 'right 10px center'};
          padding-inline-end: 28px;
        }
        .srch-detail-panel { animation: slideInDetail 0.2s ease; }
        @keyframes slideInDetail {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .srch-empty-state {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          height: 100%; gap: 12px; padding: 40px;
        }
        .srch-filters-col::-webkit-scrollbar { width: 4px; }
        .srch-filters-col::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
        .srch-divider { width: 5px; flex-shrink: 0; cursor: col-resize; background: transparent; position: relative; z-index: 10; transition: background 0.15s; }
        .srch-divider:hover, .srch-divider:active { background: ${T.border}; }
        .srch-divider::after { content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 1px; height: 32px; background: ${T.border}; border-radius: 1px; }
      ` }} />

      {/* ─── Top Bar (Search + Sort) ─────────────────────────────────────── */}
      <div style={{
        padding: '16px 20px 12px', borderBottom: `1px solid ${T.border}`,
        display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0,
      }}>
        {/* Search row */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="search-input-wrap">
            <FaSearch size={14} color={T.textMuted} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('app.search.placeholder')}
              autoFocus
            />
            {query && (
              <FaTimes size={12} color={T.textMuted} style={{ cursor: 'pointer' }} onClick={() => setQuery('')} />
            )}
          </div>
        </div>

        {/* Sort row + result count + platform badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {sortAsc ? <FaSortAmountUp size={11} color={T.textMuted} /> : <FaSortAmountDown size={11} color={T.textMuted} />}
          {SORT_OPTIONS.map(s => (
            <div
              key={s}
              className={`srch-sort-pill${sortBy === s ? ' active' : ''}`}
              onClick={() => {
                if (sortBy === s) setSortAsc(prev => !prev);
                else { setSortBy(s); setSortAsc(false); }
              }}
              style={sortBy === s ? { display: 'flex', alignItems: 'center', gap: 4 } : {}}
            >
              {t(`app.search.sort_${s}`)}
              {sortBy === s && (sortAsc ? <FaSortAmountUp size={9} /> : <FaSortAmountDown size={9} />)}
            </div>
          ))}
          {hasSearched && totalCount > 0 && (
            <span style={{ marginInlineStart: 'auto', color: T.textMuted, fontSize: 11 }}>
              {t('app.search.resultCount', { count: totalCount })}
            </span>
          )}
          {hasSearched && totalCount > 0 && Object.entries(platformCounts).map(([src, count]) => {
            const Icon = PLATFORM_ICONS[src] || FaGlobe;
            const color = PLATFORM_COLORS[src] || '#8e8e93';
            return (
              <span key={src} style={{
                display: 'flex', alignItems: 'center', gap: 3,
                padding: '2px 7px', borderRadius: 12,
                background: `${color}12`, fontSize: 10, fontWeight: 600, color,
              }}>
                <Icon size={9} /> {count}
              </span>
            );
          })}
        </div>
      </div>

      {/* ─── Main Content (3-column: Filters | Results | Detail) ──────────── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ─── Filters Column (leading side) ─────────────────────────────── */}
        <div
          className="srch-filters-col"
          style={{
            width: filtersW, flexShrink: 0,
            overflowX: 'hidden', overflowY: 'auto',
            padding: '16px 14px',
            display: 'flex', flexDirection: 'column', gap: 18,
            background: T.card, minWidth: 0,
          }}
        >
          {/* Platforms */}
          <div>
            <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 8 }}>
              {t('app.search.platforms')}
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {ALL_PLATFORMS.map(p => {
                const Icon = PLATFORM_ICONS[p] || FaGlobe;
                const color = PLATFORM_COLORS[p] || '#8e8e93';
                const active = activePlatforms.includes(p);
                return (
                  <div
                    key={p}
                    className={`srch-pill${active ? ' active' : ''}`}
                    onClick={() => togglePlatform(p)}
                    style={{
                      ...(active ? { borderColor: color, background: `${color}12`, color } : {}),
                      padding: '8px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={18} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Emotions */}
          <div>
            <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 8 }}>
              {t('app.search.emotions')}
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {ALL_EMOTIONS.map(e => {
                const color = EMOTION_COLORS[e];
                const active = activeEmotions.includes(e);
                return (
                  <div
                    key={e}
                    className={`srch-pill${active ? ' active' : ''}`}
                    onClick={() => toggleEmotion(e)}
                    style={active ? { borderColor: color, background: `${color}15`, color } : {}}
                  >
                    {t(`app.search.emotion_${e}`)}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sentiment — dual-thumb on single track */}
          <div>
            <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 8 }}>
              {t('app.search.sentimentRange')}
            </div>
            <div style={{ position: 'relative', height: 28, marginBottom: 4 }}>
              {/* Track background */}
              <div style={{
                position: 'absolute', top: 12, left: 0, right: 0, height: 6, borderRadius: 3,
                background: isDark ? '#2e3140' : '#f3f4f6',
              }} />
              {/* Active range fill */}
              <div style={{
                position: 'absolute', top: 12, height: 6, borderRadius: 3,
                left: `${((sentimentRange[0] + 1) / 2) * 100}%`,
                right: `${(1 - (sentimentRange[1] + 1) / 2) * 100}%`,
                background: T.primary,
              }} />
              {/* Min thumb */}
              <input
                type="range" min="-100" max="100" value={sentimentRange[0] * 100}
                onChange={(e) => {
                  const v = Number(e.target.value) / 100;
                  if (v < sentimentRange[1]) setSentimentRange([v, sentimentRange[1]]);
                }}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 2 }}
              />
              {/* Max thumb */}
              <input
                type="range" min="-100" max="100" value={sentimentRange[1] * 100}
                onChange={(e) => {
                  const v = Number(e.target.value) / 100;
                  if (v > sentimentRange[0]) setSentimentRange([sentimentRange[0], v]);
                }}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 3 }}
              />
              {/* Visual thumbs */}
              <div style={{
                position: 'absolute', top: 7,
                left: `calc(${((sentimentRange[0] + 1) / 2) * 100}% - 7px)`,
                width: 14, height: 14, borderRadius: '50%',
                background: '#ff453a', border: `2px solid ${T.card}`,
                pointerEvents: 'none',
              }} />
              <div style={{
                position: 'absolute', top: 7,
                left: `calc(${((sentimentRange[1] + 1) / 2) * 100}% - 7px)`,
                width: 14, height: 14, borderRadius: '50%',
                background: '#30d158', border: `2px solid ${T.card}`,
                pointerEvents: 'none',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: T.textMuted }}>
              <span style={{ color: '#ff453a' }}>{sentimentRange[0].toFixed(1)}</span>
              <span style={{ color: '#8e8e93' }}>0</span>
              <span style={{ color: '#30d158' }}>{sentimentRange[1].toFixed(1)}</span>
            </div>
          </div>

          {/* Date Range */}
          <div>
            <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 8 }}>
              {t('app.search.dateRange')}
            </div>
            <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
              <input type="date" className="srch-filter-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              <input type="date" className="srch-filter-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>

          {/* Author */}
          <div>
            <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 8 }}>
              {t('app.search.author')}
            </div>
            <input
              className="srch-filter-input"
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
              placeholder={t('app.search.authorPlaceholder')}
            />
          </div>

          {/* Engagement */}
          <div>
            <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 8 }}>
              {t('app.search.engagement')}
            </div>
            <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
              <input className="srch-filter-input" type="number" value={minLikes} onChange={(e) => setMinLikes(e.target.value)} placeholder={t('app.search.minLikes')} />
              <input className="srch-filter-input" type="number" value={minViews} onChange={(e) => setMinViews(e.target.value)} placeholder={t('app.search.minViews')} />
            </div>
          </div>

          {/* List */}
          {lists.length > 0 && (
            <div>
              <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 8 }}>
                {t('app.search.list')}
              </div>
              <select className="srch-filter-select" value={selectedList} onChange={(e) => setSelectedList(e.target.value)}>
                <option value="">{t('app.common.all')}</option>
                {lists.map(l => <option key={l.id || l.name} value={l.name}>{l.name}</option>)}
              </select>
            </div>
          )}

          {/* List Role */}
          <div>
            <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 8 }}>
              {t('app.search.listRole') || 'List Role'}
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[
                { value: 'brand', label: t('app.search.roleBrand') || 'Brand', color: '#0a84ff' },
                { value: 'competitor', label: t('app.search.roleCompetitor') || 'Competitor', color: '#ff453a' },
                { value: 'general', label: t('app.search.roleGeneral') || 'General', color: '#8e8e93' },
              ].map(role => {
                const active = selectedListRoles.includes(role.value);
                return (
                  <button key={role.value} className="srch-pill"
                    onClick={() => setSelectedListRoles(prev => active ? prev.filter(r => r !== role.value) : [...prev, role.value])}
                    style={{
                      padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 600,
                      border: `1px solid ${active ? role.color + '60' : T.border}`,
                      background: active ? role.color + '15' : 'transparent',
                      color: active ? role.color : T.textMuted,
                    }}
                  >{role.label}</button>
                );
              })}
            </div>
          </div>

          {/* Clear filters */}
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                padding: '8px 12px', borderRadius: 8,
                background: T.surface,
                border: `1px solid ${T.border}`, cursor: 'pointer',
                color: T.error, fontSize: 11, fontWeight: 600,
                marginTop: 'auto',
              }}
            >
              <FaTimes size={9} />
              {t('app.search.clearFilters')} ({activeFilterCount})
            </button>
          )}
        </div>

        {/* Divider: Filters | Results */}
        <div className="srch-divider" onMouseDown={startDrag('filters')} />

        {/* ─── Results Column (middle) ───────────────────────────────────── */}
        <div
          ref={listRef}
          className="srch-results-list"
          onScroll={handleScroll}
          style={{
            width: resultsW, flexShrink: 0,
            overflow: 'auto',
          }}
        >
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
              <ActivityIndicator size="large" color={T.primary} />
            </div>
          ) : !hasSearched ? (
            <div className="srch-empty-state">
              <FaSearch size={32} color={T.textMuted} style={{ opacity: 0.4 }} />
              <span style={{ color: T.textMuted, fontSize: 14 }}>{t('app.search.emptyPrompt')}</span>
              <span style={{ color: T.textMuted, fontSize: 12, textAlign: 'center', maxWidth: 260 }}>{t('app.search.emptyHint')}</span>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="srch-empty-state">
              <FaSearch size={28} color={T.textMuted} style={{ opacity: 0.3 }} />
              <span style={{ color: T.textMuted, fontSize: 13 }}>{t('app.search.noResults')}</span>
              <span style={{ color: T.textMuted, fontSize: 11, textAlign: 'center' }}>{t('app.search.noResultsHint')}</span>
            </div>
          ) : (
            <>
              {filteredResults.map((item, i) => (
                <SearchResultTile
                  key={`${item.source}-${item.tweet_id || item.id || i}`}
                  item={item}
                  isSelected={selectedPost && (selectedPost.tweet_id || selectedPost.id) === (item.tweet_id || item.id)}
                  onSelect={setSelectedPost}
                  highlightText={query}
                />
              ))}
              {isLoadingMore && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                  <ActivityIndicator size="small" color={T.primary} />
                </div>
              )}
              {!hasMore && filteredResults.length > 0 && (
                <div style={{ textAlign: 'center', padding: 16, color: T.textMuted, fontSize: 11 }}>
                  {t('app.search.endOfResults')}
                </div>
              )}
            </>
          )}
        </div>

        {/* Divider: Results | Detail */}
        <div className="srch-divider" onMouseDown={startDrag('results')} />

        {/* ─── Detail Panel (trailing side) ──────────────────────────────── */}
        <div style={{ flex: 1, overflow: 'hidden', background: T.bg }}>
          {selectedPost ? (
            <div className="srch-detail-panel" style={{ height: '100%' }}>
              <PostDetailView
                post={selectedPost}
                listId={selectedPost.list_id}
                listName={selectedPost.list_name}
                onClose={() => setSelectedPost(null)}
                onDraftReaction={(post) => setSelectedPost(post)}
                onRemovePost={(post) => setSelectedPost(null)}
                t={t}
                highlightText={query}
              />
            </div>
          ) : (
            <div className="srch-empty-state">
              <FaSearch size={28} color={T.textMuted} style={{ opacity: 0.25 }} />
              <span style={{ color: T.textMuted, fontSize: 13 }}>{t('app.search.selectPost')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchScreen;
